/**
 * ProShip Direct v2 — live label preview.
 *
 * A structural preview, not a print-accurate render: it exists so a merchant can see what each
 * toggle removes before they print a thousand labels and find out. Blocks appear and disappear
 * exactly as the field switches say, which is the whole point — a settings screen that lists
 * twenty-six checkboxes and shows nothing is a screen you have to test on real parcels.
 *
 * Sample values are fixed, so two people looking at the same config see the same label.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { NAVY, SUBTLE, BORDER, WHITE, LIGHT_BG } from '../../../styles/foundation/colors';
import { FONT, WEIGHT } from '../../../styles/foundation/typography';
import { MONO } from './directCellStyles';

const SAMPLE = {
  awb: '31049539170746',
  orderId: 'KOLK00669',
  invoiceNumber: 'INV-2026-0417',
  orderDate: '23 Jul 2026',
  invoiceDate: '23 Jul 2026',
  value: '1,299',
  routing: 'BLR/KA',
  buyer: 'Rahul Sharma',
  address: '12, MG Road, Near City Mall, Indiranagar',
  city: 'Bengaluru | Karnataka |',
  pincode: '560038',
  phone: '98765 43210',
  masked: '98••• •3210',
  seller: 'Prozo Warehouse, Kolkata',
  gstin: '29ABCDE1234F1Z5',
  courier: 'Delhivery Surface',
  mode: 'Surface',
  service: 'Forward',
  weight: '850 g',
  dims: '25 × 18 × 6 cm',
  rtoAddress: 'Plot 5, Sector 5, Bidhannagar, Kolkata 700091',
  rtoContact: '75498 30612',
  items: [
    { name: 'Cotton T-Shirt (Blue, L)', sku: 'TSHIRT-BL-L', qty: 2, cost: '499' },
    { name: 'Slim-Fit Denim Jeans (32)', sku: 'JEANS-SLM-32', qty: 1, cost: '1,299' },
    { name: 'Running Shoes (UK 9)', sku: 'SHOE-RUN-UK9', qty: 1, cost: '2,499' },
  ],
};

const t = (size, weight = 'regular', color = NAVY) => ({
  fontFamily: FONT,
  fontSize: size,
  fontWeight: WEIGHT[weight],
  color,
  lineHeight: 1.35,
});

const cellSx = { border: `1px solid ${NAVY}`, px: 0.5, py: 0.25 };

/** Fake barcode: deterministic bar widths from the payload, so it looks like a real symbology. */
function Barcode({ value, height = 34 }) {
  const bars = value.split('').flatMap((ch, i) => {
    const n = ch.charCodeAt(0) + i;
    return [
      { w: (n % 3) + 1, filled: true },
      { w: (n % 2) + 1, filled: false },
    ];
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', height, gap: 0, justifyContent: 'center' }}>
      {bars.map((bar, i) => (
        <Box
          key={`${value}-${i}`}
          sx={{ width: `${bar.w}px`, bgcolor: bar.filled ? NAVY : 'transparent' }}
        />
      ))}
    </Box>
  );
}

export default function LabelPreview({ config, scale = 1 }) {
  const on = (key) => Boolean(config.fields[key]);
  const isCompact = config.size === '4x4';

  return (
    <Box
      sx={{
        width: 300 * scale,
        border: `1px dashed ${NAVY}`,
        bgcolor: WHITE,
        p: 0.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        mx: 'auto',
      }}
    >
      {/* Header: brand + custom text */}
      {(on('brandLogo') || config.headerText) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={t(8, 'semibold', SUBTLE)}>{config.headerText}</Typography>
          {on('brandLogo') && (
            <Box sx={{ px: 1, py: 0.4, bgcolor: LIGHT_BG, border: `1px solid ${BORDER}` }}>
              <Typography sx={t(7, 'bold', SUBTLE)}>LOGO</Typography>
            </Box>
          )}
        </Box>
      )}

      {on('paymentModeHeader') && (
        <Box sx={{ bgcolor: LIGHT_BG, px: 0.5 }}>
          <Typography sx={t(8, 'bold')}>PREPAID</Typography>
        </Box>
      )}

      {/* Delivery block + routing */}
      {on('deliveryAddress') && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={t(8, 'bold')}>DELIVERY ADDRESS:</Typography>
            <Typography sx={t(8, 'semibold')}>{SAMPLE.buyer}</Typography>
            <Typography sx={t(7.5)}>{SAMPLE.address}</Typography>
            <Typography sx={t(7.5)}>
              {SAMPLE.city} <Box component='span' sx={{ bgcolor: LIGHT_BG, px: 0.3 }}>{SAMPLE.pincode}</Box>
            </Typography>
            {on('deliveryContact') && (
              <Typography sx={t(7.5)}>
                Contact: {on('maskContact') ? SAMPLE.masked : SAMPLE.phone}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            {on('orderDate') && <Typography sx={t(7.5)}>Order Date: <b>{SAMPLE.orderDate}</b></Typography>}
            {on('invoiceDate') && <Typography sx={t(7.5)}>Invoice Date: <b>{SAMPLE.invoiceDate}</b></Typography>}
            {on('rtoRoutingCode') && <Typography sx={t(13, 'bold')}>{SAMPLE.routing}</Typography>}
          </Box>
        </Box>
      )}

      {/* Courier strip */}
      {(on('courierName') || on('shippingMode') || on('serviceType') || on('declaredValue') || on('deadWeight') || on('dimensions')) && (
        <Box sx={{ bgcolor: LIGHT_BG, border: `1px solid ${BORDER}`, px: 0.5, py: 0.3, display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1 }}>
          <Typography sx={t(7.5)}>Courier AWB No: <b>{SAMPLE.awb}</b></Typography>
          {on('declaredValue') && <Typography sx={t(7.5)}>Total Price: <b>{SAMPLE.value}</b></Typography>}
          {on('courierName') && <Typography sx={t(7.5)}>Courier Name: <b>{SAMPLE.courier}</b></Typography>}
          {on('shippingMode') && <Typography sx={t(7.5)}>Mode of Shipping: <b>{SAMPLE.mode}</b></Typography>}
          {on('serviceType') && <Typography sx={t(7.5)}>Service Type: <b>{SAMPLE.service}</b></Typography>}
          {on('deadWeight') && <Typography sx={t(7.5)}>Dead Weight: <b>{SAMPLE.weight}</b></Typography>}
          {on('dimensions') && <Typography sx={t(7.5)}>Dimensions: <b>{SAMPLE.dims}</b></Typography>}
        </Box>
      )}

      {/* Primary barcode */}
      {(on('orderBarcode') || on('invoiceBarcode')) && (
        <Box>
          <Barcode value={SAMPLE.awb} height={isCompact ? 26 : 34} />
          <Typography sx={{ ...t(9, 'bold'), textAlign: 'center', fontFamily: MONO }}>{SAMPLE.awb}</Typography>
        </Box>
      )}

      {/* Sold by */}
      {(on('sellerBrandName') || on('gstin') || on('shipperAddress') || on('shipperPhone')) && (
        <Box sx={{ border: `1px solid ${NAVY}`, px: 0.5, py: 0.25 }}>
          <Typography sx={t(7.5)}>
            {on('sellerBrandName') && <>Sold By: <b>{SAMPLE.seller}</b></>}
            {on('gstin') && <> | GSTIN: <b>{SAMPLE.gstin}</b></>}
          </Typography>
          {on('shipperPhone') && <Typography sx={t(7.5)}>Shipper: {SAMPLE.rtoContact}</Typography>}
        </Box>
      )}

      {/* Items */}
      {on('itemName') && !isCompact && (
        <Box sx={{ display: 'grid', gridTemplateColumns: `1fr ${on('skuCode') ? 'auto' : ''} ${on('productQuantity') ? 'auto' : ''} ${on('productCost') ? 'auto' : ''}`.trim() }}>
          <Typography sx={{ ...t(7.5, 'bold'), ...cellSx }}>Description</Typography>
          {on('skuCode') && <Typography sx={{ ...t(7.5, 'bold'), ...cellSx }}>SKU</Typography>}
          {on('productQuantity') && <Typography sx={{ ...t(7.5, 'bold'), ...cellSx }}>Qty</Typography>}
          {on('productCost') && <Typography sx={{ ...t(7.5, 'bold'), ...cellSx }}>Cost</Typography>}
          {SAMPLE.items.map((item) => (
            <React.Fragment key={item.sku}>
              <Typography sx={{ ...t(7.5), ...cellSx }}>{item.name}</Typography>
              {on('skuCode') && <Typography sx={{ ...t(7.5), ...cellSx, fontFamily: MONO }}>{item.sku}</Typography>}
              {on('productQuantity') && <Typography sx={{ ...t(7.5), ...cellSx }}>{item.qty}</Typography>}
              {on('productCost') && <Typography sx={{ ...t(7.5), ...cellSx }}>{item.cost}</Typography>}
            </React.Fragment>
          ))}
        </Box>
      )}

      {on('otherCharges') && (
        <Typography sx={t(7.5)}>Other charges: <b>₹49</b> (shipping)</Typography>
      )}

      {/* Handover + secondary barcode */}
      {on('trackingNumber') && (
        <Box>
          <Box sx={{ bgcolor: NAVY, px: 0.5, display: 'inline-block' }}>
            <Typography sx={t(7.5, 'bold', WHITE)}>Handover to {SAMPLE.courier}</Typography>
          </Box>
          <Typography sx={t(7.5)}>Tracking ID: {SAMPLE.awb}</Typography>
          {!isCompact && (
            <>
              <Barcode value={SAMPLE.orderId} height={26} />
              <Typography sx={{ ...t(8, 'bold'), textAlign: 'center', fontFamily: MONO }}>{SAMPLE.orderId}</Typography>
            </>
          )}
        </Box>
      )}

      {on('orderId') && <Typography sx={t(7.5)}>Order ID: <b>{SAMPLE.orderId}</b></Typography>}
      {on('invoiceNumber') && <Typography sx={t(7.5)}>Invoice No: <b>{SAMPLE.invoiceNumber}</b></Typography>}

      {/* Return block */}
      {(on('rtoAddress') || on('rtoContact')) && (
        <Box sx={{ border: `1px solid ${BORDER}`, px: 0.5, py: 0.25 }}>
          {on('rtoAddress') && <Typography sx={t(7.5)}>Return Address: {SAMPLE.rtoAddress}</Typography>}
          {on('rtoContact') && <Typography sx={t(7.5)}>Contact Number: {SAMPLE.rtoContact}</Typography>}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {on('labelPagination') && <Typography sx={t(7, 'regular', SUBTLE)}>1 of 1</Typography>}
        {config.footerText && (
          <Typography sx={{ ...t(7, 'regular', SUBTLE), ml: 'auto' }}>{config.footerText}</Typography>
        )}
      </Box>
    </Box>
  );
}
